// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { inject, observer } from "../../../stores/mobx-compat";

import PageHeader from '../../ui/PageHeader';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import IconButton from '../../ui/IconButton';
import Input from '../../ui/Input';
import Pill from '../../ui/Pill';
import Chip from '../../ui/Chip';
import Modal, { ModalActions } from '../../ui/Modal';
import DropdownMenu, {
  MenuItem, MenuSection, MenuDivider,
} from '../../ui/DropdownMenu';
import Table, {
  Th, Td, Tr, TableToolbar, TablePager,
} from '../../ui/Table';
import {
  SearchIcon, MoreVerticalIcon, EyeIcon, DownloadIcon, ExternalLinkIcon,
  RefreshIcon, UsersIcon,
} from '../../ui/icons';

import { TABLE_ITEMS_PER_PAGE, clientStatusType } from '../../../enums';
import { FIREBASE_CONFIGS } from '../../../configs';

const { projectId } = FIREBASE_CONFIGS;
const OPS = { STATUS_CHANGE: 'STATUS_CHANGE', MANAGE_USERS: 'MANAGE_USERS' };

const STATUS_TONE = {
  enabled: 'green',
  disabled: 'gray',
  archived: 'red',
};

class Admin extends Component {
  state = {
    modalOpen: false,
    operation: null,
    activePage: 1,
    statusChange: { clientId: null, statusTo: null },
    userManagement: { clientId: null },
    search: '',
    statusFilter: [...Object.keys(clientStatusType)],
    sortState: { column: 'Account Created', direction: 'descending' },
  }

  componentDidMount() {
    this.props.store.admin.getClientData();
    this.delayedSearch = _.debounce((value) => {
      this.setState({ search: value, activePage: 1 });
    }, 300);
  }

  get sortState() { return this.state.sortState; }

  handleSort = (clickedColumn) => {
    this.setState((s) => {
      if (s.sortState.column !== clickedColumn) {
        return { sortState: { column: clickedColumn, direction: 'descending' } };
      }
      return {
        sortState: {
          column: s.sortState.column,
          direction: s.sortState.direction === 'ascending' ? 'descending' : 'ascending',
        },
      };
    });
  }

  resetFilter = () => this.setState({ statusFilter: [...Object.keys(clientStatusType)] });
  removeFilter = (status) => this.setState({ statusFilter: this.state.statusFilter.filter((v) => v !== status) });

  sendResetEmail = (email) => {
    this.props.store.firebase.auth().sendPasswordResetEmail(email).then(() => {
      this.props.store.success(`Reset email sent to ${email}`);
    }).catch(() => {
      this.props.store.failure('Reset email could not be sent.');
    });
  }

  filteredAndSorted = () => {
    const { clientData } = this.props.store.admin;
    const { column, direction } = this.sortState;
    const search = this.state.search.toUpperCase();

    const filtered = Object.values(clientData).filter((item) => {
      if (!item.id || item.id.includes('superadmin')) return false;
      if (item.accountStatus && !this.state.statusFilter.includes(item.accountStatus)) return false;
      if (!search) return true;
      if (item.company && item.company.toUpperCase().includes(search)) return true;
      const users = item.users || {};
      for (const uid in users) {
        const u = users[uid];
        if (u.email && u.email.toUpperCase().includes(search)) return true;
        if (u.fullName && u.fullName.toUpperCase().includes(search)) return true;
        if (u.phone && u.phone.includes(this.state.search)) return true;
      }
      return false;
    });

    const cmp = (a, b) => {
      switch (column) {
        case 'Client': {
          const ac = (a.company || '').toUpperCase();
          const bc = (b.company || '').toUpperCase();
          return ac > bc ? 1 : ac < bc ? -1 : 0;
        }
        case 'Status':
          return (a.accountStatus || '') > (b.accountStatus || '') ? 1 : -1;
        case 'Account Created':
          return moment(a.accountCreated || 0).diff(moment(b.accountCreated || 0));
        case 'Forms Started':
          return Number(a.formsStarted || 0) - Number(b.formsStarted || 0);
        case 'Abandons':
          return Number(a.abandons || 0) - Number(b.abandons || 0);
        case 'Abandons w/ Email':
          return Number(a.abandons_with_emails || 0) - Number(b.abandons_with_emails || 0);
        case 'Abandons w/o Email':
          return (Number(a.abandons || 0) - Number(a.abandons_with_emails || 0))
            - (Number(b.abandons || 0) - Number(b.abandons_with_emails || 0));
        default: return 0;
      }
    };
    filtered.sort((a, b) => (direction === 'ascending' ? cmp(a, b) : -cmp(a, b)));
    return filtered;
  }

  modal = () => {
    const { operation, statusChange, userManagement } = this.state;
    const { clientData } = this.props.store.admin;

    if (operation === OPS.STATUS_CHANGE) {
      const company = clientData[statusChange.clientId]?.company || statusChange.clientId;
      return (
        <Modal
          open={this.state.modalOpen}
          onClose={() => this.setState({ modalOpen: false })}
          size="sm"
          title="Change client status"
          actions={(
            <ModalActions
              onCancel={() => this.setState({ modalOpen: false })}
              onConfirm={async () => {
                await this.props.store.admin.setClientStatus(statusChange.clientId, statusChange.statusTo);
                this.setState({ modalOpen: false });
              }}
              confirmLabel={`Set to ${statusChange.statusTo}`}
              confirmVariant={statusChange.statusTo === 'archived' ? 'danger' : 'primary'}
            />
          )}
        >
          <p className="m-0 text-[13px] text-mov-ink-soft">
            Change <b className="text-mov-ink">{company}</b>&apos;s status to{' '}
            <Pill tone={STATUS_TONE[statusChange.statusTo]} dot>{statusChange.statusTo}</Pill>?
          </p>
        </Modal>
      );
    }

    if (operation === OPS.MANAGE_USERS) {
      const client = clientData[userManagement.clientId] || {};
      const users = Object.entries(client.users || {});
      return (
        <Modal
          open={this.state.modalOpen}
          onClose={() => this.setState({ modalOpen: false })}
          size="lg"
          title={`${client.company || userManagement.clientId} · users`}
          icon={UsersIcon}
        >
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Role</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <Tr><Td colSpan={5} mute>No users on file.</Td></Tr>
              )}
              {users.map(([uid, user]) => (
                <Tr key={uid}>
                  <Td>{user.fullName}</Td>
                  <Td>{user.email}</Td>
                  <Td>{user.phone}</Td>
                  <Td>{user.claims?.role}</Td>
                  <Td align="right">
                    <Button size="sm" onClick={() => this.sendResetEmail(user.email)}>
                      Reset password
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Modal>
      );
    }
    return null;
  }

  rowMenu = (client) => (
    <DropdownMenu
      align="right"
      width={220}
      trigger={<IconButton variant="ghost" icon={MoreVerticalIcon} ariaLabel="Row actions" />}
    >
      <MenuSection>{client.company || client.id}</MenuSection>
      <MenuSection>Set status</MenuSection>
      <MenuItem onSelect={() => this.setState({
        operation: OPS.STATUS_CHANGE,
        statusChange: { clientId: client.id, statusTo: 'enabled' },
        modalOpen: true,
      })}><Pill tone="green" dot>Enabled</Pill></MenuItem>
      <MenuItem onSelect={() => this.setState({
        operation: OPS.STATUS_CHANGE,
        statusChange: { clientId: client.id, statusTo: 'disabled' },
        modalOpen: true,
      })}><Pill tone="gray" dot>Disabled</Pill></MenuItem>
      <MenuItem onSelect={() => this.setState({
        operation: OPS.STATUS_CHANGE,
        statusChange: { clientId: client.id, statusTo: 'archived' },
        modalOpen: true,
      })}><Pill tone="red" dot>Archived</Pill></MenuItem>
      <MenuDivider />
      <MenuSection>Manage</MenuSection>
      <MenuItem icon={UsersIcon} onSelect={() => this.setState({
        operation: OPS.MANAGE_USERS,
        userManagement: { clientId: client.id },
        modalOpen: true,
      })}>Manage users</MenuItem>
      <MenuItem icon={DownloadIcon} onSelect={() => this.props.store.admin.downloadCaptures(client.id, client.company)}>
        Download captures
      </MenuItem>
      <MenuItem icon={EyeIcon} onSelect={() => this.props.store.admin.spoofClient(client.id)}>
        Spoof account
      </MenuItem>
      <MenuDivider />
      <MenuItem
        icon={ExternalLinkIcon}
        onSelect={() => window.open(`https://console.firebase.google.com/u/0/project/${projectId}/database/${projectId}/data/clients/${client.id}`, '_blank')}
      >
        Open in Firebase
      </MenuItem>
    </DropdownMenu>
  )

  render() {
    const sorted = this.filteredAndSorted();
    const totalPages = Math.ceil(sorted.length / TABLE_ITEMS_PER_PAGE) || 1;
    const start = (this.state.activePage - 1) * TABLE_ITEMS_PER_PAGE;
    const slice = sorted.slice(start, start + TABLE_ITEMS_PER_PAGE);
    const { column, direction } = this.sortState;

    const sortDir = (col) => (direction === 'ascending' ? 'asc' : 'desc');
    const dir = (col) => (column === col ? sortDir(col) : 'asc');
    const colHeader = (col, align = 'left') => (
      <Th
        sortable
        active={column === col}
        direction={dir(col)}
        onSort={() => this.handleSort(col)}
        align={align}
      >
        {col}
      </Th>
    );

    return (
      <>
        <PageHeader
          eyebrow="Admin"
          title={<>All clients, <em>one</em> view.</>}
          sub="Search, filter, and act on every workspace in the system."
          actions={(
            <Input
              leftIcon={SearchIcon}
              placeholder="Search clients, emails, phones…"
              onChange={(e) => {
                const v = e.target.value;
                this.delayedSearch(v);
              }}
              style={{ minWidth: 320 }}
            />
          )}
        />

        <Card className="mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="mov-mono text-[11px] uppercase tracking-wider text-mov-ink-mute">Status filter</span>
            <div className="flex items-center gap-2 flex-wrap">
              {this.state.statusFilter.map((s) => (
                <Chip key={s} tone={STATUS_TONE[s] === 'red' ? 'gray' : 'green'} onRemove={() => this.removeFilter(s)}>
                  <Pill tone={STATUS_TONE[s]} dot className="!p-0 !bg-transparent">{s}</Pill>
                </Chip>
              ))}
            </div>
            <Button size="sm" variant="ghost" icon={RefreshIcon} className="ml-auto" onClick={this.resetFilter}>
              Reset
            </Button>
          </div>
        </Card>

        <Card padded={false}>
          <TableToolbar title="Clients" count={sorted.length} />
          <Table>
            <thead>
              <tr>
                {colHeader('Client')}
                <Th>Email(s)</Th>
                {colHeader('Status')}
                {colHeader('Account Created')}
                {colHeader('Forms Started', 'right')}
                {colHeader('Abandons', 'right')}
                {colHeader('Abandons w/ Email', 'right')}
                {colHeader('Abandons w/o Email', 'right')}
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {slice.map((client) => {
                const emails = Object.values(client.users || {}).map((u) => u.email).filter(Boolean);
                return (
                  <Tr key={client.id}>
                    <Td>{client.company || client.id}</Td>
                    <Td>
                      <div className="flex flex-col gap-0.5 text-[12.5px]">
                        {emails.map((e) => <span key={e}>{e}</span>)}
                      </div>
                    </Td>
                    <Td>
                      <Pill tone={STATUS_TONE[client.accountStatus] || 'gray'} dot>
                        {client.accountStatus || '—'}
                      </Pill>
                    </Td>
                    <Td mute>{client.accountCreated ? moment(client.accountCreated, 'YYYY-MM-DD').format('MMM DD, YYYY') : '—'}</Td>
                    <Td align="right" mono>{client.formsStarted ? client.formsStarted.toLocaleString() : '—'}</Td>
                    <Td align="right" mono>{client.abandons ? client.abandons.toLocaleString() : '—'}</Td>
                    <Td align="right" mono>{client.abandons_with_emails ? client.abandons_with_emails.toLocaleString() : '—'}</Td>
                    <Td align="right" mono>
                      {(client.abandons && client.abandons_with_emails)
                        ? (client.abandons - client.abandons_with_emails).toLocaleString()
                        : '—'}
                    </Td>
                    <Td align="right">{this.rowMenu(client)}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <TablePager
            page={this.state.activePage}
            totalPages={totalPages}
            total={sorted.length}
            onChange={(p) => this.setState({ activePage: p })}
          />
        </Card>

        {this.modal()}

        <a style={{ display: 'none' }} ref={(emailRef) => { this.props.store.admin.setEmailRef(emailRef); }}>csv anchor</a>
      </>
    );
  }
}

export default inject('store')(observer(Admin));
